import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, Phone, Mail, Globe } from "lucide-react";
import { LOGO_URL, CONTACT } from "../data/content";
import { useLang } from "../i18n/LanguageContext";

const NAV_KEYS = [
  { to: "/", key: "home" },
  { to: "/about", key: "about" },
  { to: "/services", key: "services" },
  { to: "/portfolio", key: "portfolio" },
  { to: "/blog", key: "blog" },
  { to: "/faq", key: "faq" },
];

export const Header = () => {
  const [open, setOpen] = useState(false);
  const { lang, toggle, t } = useLang();

  return (
    <header className="fixed top-0 left-0 right-0 z-50">
      <div className="bg-ink text-cream/80 hidden md:block">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 h-9 flex items-center justify-between font-mono text-[11px] tracking-wide">
          <div className="flex items-center gap-6">
            <a href={CONTACT.phoneHref} data-testid="topbar-phone" className="flex items-center gap-1.5 hover:text-periwinkle-light transition-colors duration-300">
              <Phone size={11} /> {CONTACT.phone}
            </a>
            <a href={`mailto:${CONTACT.email}`} data-testid="topbar-email" className="flex items-center gap-1.5 hover:text-periwinkle-light transition-colors duration-300">
              <Mail size={11} /> {CONTACT.email}
            </a>
          </div>
          <div className="flex items-center gap-6">
            <span>{CONTACT.location}</span>
            <span>{CONTACT.hours[lang]}</span>
          </div>
        </div>
      </div>
      <div className="bg-cream/85 backdrop-blur-xl border-b border-ink/5">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 h-20 flex items-center justify-between">
        <Link to="/" data-testid="header-logo-link" className="flex items-center gap-3">
          <img src={LOGO_URL} alt="SG Language Services LLC logo" className="h-12 w-12 rounded-full object-cover" />
          <span className="font-serif text-xl font-semibold tracking-tight leading-none">
            SG Language Services
            <span className="block font-mono text-[10px] uppercase tracking-[0.25em] text-muted2 font-normal mt-0.5">{t.nav.tagline}</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {NAV_KEYS.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-link-${n.key}`}
              className={({ isActive }) =>
                `link-underline text-sm font-medium transition-colors duration-300 ${isActive ? "text-periwinkle-deep" : "text-ink/70 hover:text-ink"}`
              }
            >
              {t.nav[n.key]}
            </NavLink>
          ))}
          <button
            onClick={toggle}
            data-testid="lang-switcher"
            className="flex items-center gap-1.5 rounded-full border border-ink/20 px-4 py-2 font-mono text-xs font-semibold uppercase tracking-wider hover:border-periwinkle-deep hover:text-periwinkle-deep transition-colors duration-300"
            aria-label="Switch language"
          >
            <Globe size={13} /> {lang === "en" ? "ES" : "EN"}
          </button>
          <Link
            to="/contact"
            data-testid="header-quote-btn"
            className="rounded-full bg-ink text-cream px-6 py-2.5 text-sm font-semibold hover:bg-periwinkle-deep transition-colors duration-300"
          >
            {t.nav.quote}
          </Link>
        </nav>

        <div className="lg:hidden flex items-center gap-2">
          <button
            onClick={toggle}
            data-testid="lang-switcher-mobile"
            className="flex items-center gap-1 rounded-full border border-ink/20 px-3 py-1.5 font-mono text-xs font-semibold uppercase"
            aria-label="Switch language"
          >
            <Globe size={12} /> {lang === "en" ? "ES" : "EN"}
          </button>
          <button
            data-testid="mobile-menu-btn"
            onClick={() => setOpen(!open)}
            className="p-2 text-ink"
            aria-label="Toggle menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>
      </div>

      {open && (
        <nav data-testid="mobile-menu" className="lg:hidden bg-cream border-t border-ink/5 px-6 py-6 flex flex-col gap-4">
          {NAV_KEYS.map((n) => (
            <NavLink key={n.to} to={n.to} onClick={() => setOpen(false)} className="text-lg font-medium text-ink/80">
              {t.nav[n.key]}
            </NavLink>
          ))}
          <Link to="/contact" onClick={() => setOpen(false)} className="rounded-full bg-ink text-cream px-6 py-3 text-center font-semibold">
            {t.nav.quote}
          </Link>
        </nav>
      )}
    </header>
  );
};
